import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import clsx from 'clsx';

/**
 * Generic breadcrumb. Each item is either a clickable link (`to`) or a
 * read-only label (no `to`). Slot the `extra` node at the right edge of
 * the trail for things like the visibility / lease badges.
 *
 * We keep this dumb on purpose: the `DeviceDetail` shell composes its
 * own trail (`设备列表 / 设备名 / 当前 tab`) and feeds it in. That keeps
 * the route → label mapping next to the route declaration instead of
 * scattered through utility files.
 */
export interface BreadcrumbItem {
  label: React.ReactNode;
  to?: string;
}

export function Breadcrumb({
  items,
  extra,
  className,
}: {
  items: ReadonlyArray<BreadcrumbItem>;
  extra?: React.ReactNode;
  className?: string;
}) {
  return (
    <nav className={clsx('flex min-w-0 flex-1 items-center gap-1.5 text-xs', className)} aria-label="breadcrumb">
      <ol className="flex min-w-0 flex-1 items-center gap-1.5">
        {items.map((item, i) => (
          <Fragment key={i}>
            {i > 0 && <ChevronRight className="h-3 w-3 shrink-0 text-ink-dim" />}
            <li className={clsx('min-w-0', i === items.length - 1 && 'truncate')}>
              {item.to ? (
                <Link to={item.to} className="text-ink-muted hover:text-accent">
                  {item.label}
                </Link>
              ) : (
                <span className={clsx(i === items.length - 1 ? 'text-ink' : 'text-ink-muted')}>{item.label}</span>
              )}
            </li>
          </Fragment>
        ))}
      </ol>
      {extra && <div className="ml-2 flex items-center gap-1.5">{extra}</div>}
    </nav>
  );
}
